import { useState, useEffect } from "react";
import DashboardLayout from "../Layout/DashboardLayout";
import { Package, Plus, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import TrTableProducts from "../element/TrTableProducts";
import AddProduct from "../Component/AddProduct";
import EditProduct from '../Component/EditProduct';
import axios from "axios";
import { BASE_URL, EXTERNAL_BASE_URL } from '../apiConfig';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import { useTranslation } from '../LanguageContext';
import { useAuth } from '../AuthContext';

export default function Page() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [products, setProducts] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [totalProducts, setTotalProducts] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [loading, setLoading] = useState(true)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSyncProducts, setIsSyncProducts] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const token = Cookies.get('WEB_SSR');

  const fetchProducts = async () => {
    try {
      const params = {
        page: currentPage,
        search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      }

      const response = await axios.get(`${BASE_URL}/api/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        params
      })
      
      if (response.data.success) {
        setProducts(response.data.data.data || [])
        setLastPage(response.data.data.last_page || 1)
        setTotalProducts(response.data.data.total || 0)
        setPerPage(response.data.data.per_page || 10)
      }
    } catch (error) {
      console.error('Error fetching products:', error)
      toast.error(t('products.fetch.error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [currentPage, search, statusFilter])

  useEffect(() => {
    const handleStockUpdate = () => {
      fetchProducts();
    };
    window.addEventListener('stock-updated', handleStockUpdate);
    return () => window.removeEventListener('stock-updated', handleStockUpdate);
  }, []);

  const confirmDelete = (product) => {
    setProductToDelete(product)
    setShowDeleteModal(true)
  }

  const handleDeleteProduct = async () => {
    if (!productToDelete) return
    
    setIsDeleting(true)
    try {
      const response = await axios.delete(`${BASE_URL}/api/products/${productToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
      
      if (response.data.success) {
        toast.success(t('products.delete.success'))
        fetchProducts()
        setShowDeleteModal(false)
        setProductToDelete(null)
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete product')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleEditProduct = (product) => {
    setSelectedProduct(product)
    setIsEditModalOpen(true)
  }

  const handleProductAdded = () => {
    fetchProducts()
    setIsAddModalOpen(false)
  }

  const handleProductUpdated = () => {
    fetchProducts()
    setIsEditModalOpen(false)
    setSelectedProduct(null)
  }

  const syncProducts = async () => {
    try {
      setIsSyncProducts(true);
      const response = await axios.get(
        `${BASE_URL}/api/sync-products`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
  
      if (response.data.success) {
        console.log("Sync Success:", response.data);
        fetchProducts();
        const { summary, details } = response.data;
  
        console.log("Summary:", summary);
        console.log("Details:", details);
        setIsSyncProducts(false);

      } else {
        console.error("Sync failed:", response.data.message);
        setIsSyncProducts(false);
      }
    } catch (error) {
      console.error(
        "Request error:",
        error.response?.data || error.message
      );
      setIsSyncProducts(false);
    }
  };

  // Generate pagination buttons
  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisible = 5; // Number of visible page buttons
    
    if (lastPage <= 7) {
      // Show all pages if total pages are few
      for (let i = 1; i <= lastPage; i++) {
        buttons.push(i);
      }
    } else {
      // Always show first page
      buttons.push(1);
      
      if (currentPage > 3) {
        buttons.push('...');
      }
      
      // Show pages around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(lastPage - 1, currentPage + 1);
      
      if (currentPage <= 3) {
        start = 2;
        end = 4;
      }
      
      if (currentPage >= lastPage - 2) {
        start = lastPage - 3;
        end = lastPage - 1;
      }
      
      for (let i = start; i <= end; i++) {
        buttons.push(i);
      }
      
      if (currentPage < lastPage - 2) {
        buttons.push('...');
      }
      
      // Always show last page
      buttons.push(lastPage);
    }
    
    return buttons;
  };

  const goToPage = (page) => {
    if (page === '...') return;
    setCurrentPage(page);
  };

  // Calculate showing range
  const startItem = (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, totalProducts);

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-8 flex justify-between">
          <div>
            <h1 className="text-3xl max-lg:text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">{t('products.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('products.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {can('products.create') && (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="bg-[#171717] text-nowrap text-white rounded-lg text-xs py-2 px-3 flex items-center gap-2 shadow-md hover:bg-gray-800 transition-colors"
              >
                <Plus className="w-4 h-4 text-white" />  
                {t('products.add')}
              </button>
            )}
            {can('products.edit') && EXTERNAL_BASE_URL && (
              <button 
                onClick={() => setShowSyncModal(true)}
                disabled={isSyncProducts} 
                className="bg-[#171717] text-nowrap text-white rounded-lg text-xs py-2 px-3 flex items-center gap-2 shadow-md hover:bg-gray-800 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 text-white ${isSyncProducts ? 'animate-spin' : ''}`} />  
                {isSyncProducts ? t('products.syncing') : t('products.sync')}
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap gap-3">
          <input
            type="text"
            placeholder={t('products.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 w-64"
          />
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
          >
            <option value="all">{t('products.all_status')}</option>
            <option value="in_stock">{t('products.in_stock')}</option>
            <option value="low_stock">{t('products.low_stock')}</option>
            <option value="out_of_stock">{t('products.out_of_stock')}</option>
          </select>
        </div>
        

        {loading ? (
          <div>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  {/* Header Skeleton */}
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <th key={i} className="px-6 py-3">
                          <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        </th>
                      ))}
                    </tr>
                  </thead>
      
                  {/* Rows Skeleton */}
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {Array.from({ length: 6 }).map((_, rowIndex) => (
                      <tr key={rowIndex}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                            <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                          </div>
                        </td>
      
                        <td className="px-6 py-4">
                          <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        </td>
      
                        <td className="px-6 py-4">
                          <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        </td>
      
                        <td className="px-6 py-4">
                          <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        </td>
      
                        <td className="px-6 py-4">
                          <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                        </td>
      
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center gap-2">
                            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
    
            {/* Pagination Skeleton */}
            <div className="mt-6 flex items-center justify-between">
              <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="flex gap-2">
                <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                      <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-50">{t('products.table.product')}</th>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-50">{t('products.table.price')}</th>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-50">{t('products.table.stock')}</th>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-50">{t('products.table.variants')}</th>
                      <th className="px-6 py-3 text-center font-semibold text-gray-900 dark:text-gray-50">{t('products.table.status')}</th>
                      <th className="px-6 py-3 text-center font-semibold text-gray-900 dark:text-gray-50">{t('products.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {products.map((product) => (
                      <TrTableProducts 
                        key={product.id}
                        product={product}
                        onDelete={() => confirmDelete(product)}
                        onEdit={handleEditProduct}
                        onUpdate={fetchProducts}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {products.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Package className="w-20 h-20 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">{t('products.empty.title')}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  {t('products.empty.desc')}
                </p>
                <button 
                  onClick={() => setIsAddModalOpen(true)}
                  className="bg-[#171717] text-white rounded-lg text-xs py-2 px-3 flex items-center gap-1 shadow-md hover:bg-gray-800 transition-colors"
                >
                  <Plus className="w-4 h-4 text-white" />  
                  {t('products.empty.add')}
                </button>
              </div>
            ) : (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('products.pagination.showing')} {startItem} {t('products.pagination.to')} {endItem} {t('products.pagination.of')} {totalProducts} {t('products.pagination.products')}
                </p>
                <div className="flex items-center gap-1">
                  <button 
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2.5 py-2 border border-gray-200 cursor-pointer dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>

                  <div className="flex items-center gap-1 mx-1">
                    {getPaginationButtons().map((btn, index) => (
                      <button
                        key={index}
                        onClick={() => goToPage(btn)}
                        disabled={btn === '...'}
                        className={`
                          min-w-[32px] h-8 px-2 rounded-lg text-xs font-medium transition-colors
                          ${btn === currentPage 
                            ? 'bg-[#171717] text-white cursor-default' 
                            : btn === '...' 
                              ? 'cursor-default text-gray-500 dark:text-gray-400 hover:bg-transparent'
                              : 'border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer'
                          }
                        `}
                      >
                        {btn}
                      </button>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= lastPage}
                    className="px-2.5 py-2 border border-gray-200 cursor-pointer dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {showSyncModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowSyncModal(false)}>
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800" onClick={e => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-lg bg-amber-100 p-2 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
                    <RefreshCw className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('products.sync.title')}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('products.sync.confirm')}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('products.delete.cancel')}
                </button>
                <button
                  onClick={() => { setShowSyncModal(false); syncProducts() }}
                  disabled={isSyncProducts}
                  className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {t('products.sync.start')}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Add Product Modal */}
         {isAddModalOpen && (
          <AddProduct
            onClose={() => setIsAddModalOpen(false)}
            onSuccess={handleProductAdded}
          />
        )}

        {/* Edit Product Modal */}
        {isEditModalOpen && selectedProduct && (
          <EditProduct
            productToEdit={selectedProduct}
            onClose={() => {
              setIsEditModalOpen(false)
              setSelectedProduct(null)
            }}
            onSuccess={handleProductUpdated}
          />
        )}

        {showDeleteModal && productToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('products.delete.title')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('products.delete.warning')}</p>
                </div>
              </div>
              
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-gray-900 dark:text-gray-50">
                  {t('products.delete.confirm')} <span className="font-semibold">"{productToDelete.name}"</span>?
                </p>
                {productToDelete.variants && productToDelete.variants.length > 0 && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                    {t('products.delete.variant_warning')} {productToDelete.variants.length} {t('products.delete.variant_count')}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setProductToDelete(null)
                  }}
                  disabled={isDeleting}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-xs font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProduct}
                  disabled={isDeleting}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete Product'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}